import { doc, runTransaction } from 'firebase/firestore'
import { db } from '../store/firebase'
import { User } from '../types'
import {
  Allocation,
  BillPayment,
  BillingAccount,
  BillingConfig,
  BillingResult,
  CreditNote,
  PaymentMethod,
  Refund,
  SalesDocument,
} from '../types/billing'
import { BCOL, clean, commitNumbers, failure, planNumbers, writeAudit } from './billingFirestore'
import { fromN, toN } from './billingMoney'

const invRef = (id: string) => doc(db, BCOL.documents, id)

function assertApplicable(invoice: SalesDocument | undefined, accountId: string, amountN: number, label: string) {
  if (!invoice || invoice.type !== 'invoice') throw new Error(`${label}: invoice not found.`)
  if (invoice.state !== 'Issued') throw new Error(`${invoice.number ?? 'Draft'} is ${invoice.state.toLowerCase()} — only issued invoices accept payments or credits.`)
  if (invoice.accountId !== accountId) throw new Error(`${invoice.number} belongs to ${invoice.accountName}, not this account.`)
  if (amountN <= 0) throw new Error(`${invoice.number}: amount must be greater than zero.`)
  if (amountN > toN(invoice.balance)) throw new Error(`${invoice.number}: only ${invoice.balance.toFixed(2)} is outstanding.`)
}

export type PaymentInput = {
  id: string
  kind: 'Payment' | 'Deposit'
  account: BillingAccount
  date: string
  amount: number
  amountTendered: number
  method: PaymentMethod
  reference?: string
  notes?: string
  allocations: { invoiceId: string; amount: number }[]
}

/**
 * Records money received. Allocations reduce invoice balances; anything left
 * becomes account credit. Recording a card or mobile money payment only records
 * it — no customer is charged (no payment provider is integrated).
 */
export async function recordPayment(user: User, config: BillingConfig, input: PaymentInput): Promise<BillingResult> {
  try {
    const amountN = toN(input.amount)
    const tenderedN = toN(input.amountTendered || input.amount)
    const allocations = input.allocations.filter((a) => toN(a.amount) > 0)
    const allocatedN = allocations.reduce((s, a) => s + toN(a.amount), 0)
    if (amountN <= 0) return { ok: false, error: 'Enter the amount received.' }
    if (!input.date) return { ok: false, error: 'Choose the payment date.' }
    if (input.method.requiresReference && !input.reference?.trim()) return { ok: false, error: `${input.method.name} payments need a transaction reference.` }
    if (allocatedN > amountN) return { ok: false, error: 'Allocations are more than the amount received.' }
    if (tenderedN < amountN) return { ok: false, error: 'Amount tendered cannot be less than the amount received.' }
    if (input.kind === 'Deposit' && allocations.length) return { ok: false, error: 'Deposits are held as account credit and are not allocated.' }
    if (new Set(allocations.map((a) => a.invoiceId)).size !== allocations.length) return { ok: false, error: 'Each invoice can appear once per payment.' }

    const result = await runTransaction(db, async (tx) => {
      const payRef = doc(db, BCOL.payments, input.id)
      const existing = await tx.get(payRef)
      if (existing.exists()) return { already: true, number: (existing.data() as BillPayment).receiptNumber }
      const invoiceSnaps = await Promise.all(allocations.map((a) => tx.get(invRef(a.invoiceId))))
      const invoices = invoiceSnaps.map((s) => s.data() as SalesDocument | undefined)
      invoices.forEach((inv, i) => assertApplicable(inv, input.account.id, toN(allocations[i].amount), 'Payment'))
      const acctSnap = await tx.get(doc(db, BCOL.accounts, input.account.id))
      const plan = await planNumbers(tx, config, 'receipt', 1)

      const now = new Date().toISOString()
      const receiptNumber = plan.numbers[0]
      commitNumbers(tx, plan, [input.id])
      if (!acctSnap.exists()) tx.set(doc(db, BCOL.accounts, input.account.id), clean({ ...input.account, createdAt: now, createdByName: user.fullName }))
      const applied: Allocation[] = allocations.map((a, i) => {
        const inv = invoices[i]!
        tx.update(invRef(inv.id), {
          amountPaid: fromN(toN(inv.amountPaid) + toN(a.amount)),
          balance: fromN(toN(inv.balance) - toN(a.amount)),
          updatedAt: now,
        })
        return { invoiceId: inv.id, invoiceNumber: inv.number ?? '', amount: fromN(toN(a.amount)), at: now, byName: user.fullName }
      })
      const payment: BillPayment = {
        id: input.id, receiptNumber, kind: input.kind, accountId: input.account.id, accountName: input.account.name,
        date: input.date, amount: fromN(amountN), amountTendered: fromN(tenderedN), changeGiven: fromN(tenderedN - amountN),
        methodId: input.method.id, methodName: input.method.name, reference: input.reference?.trim() || undefined, notes: input.notes?.trim() || undefined,
        allocations: applied, unallocated: fromN(amountN - allocatedN), refunded: 0, state: 'Posted',
        receivedById: user.id, receivedByName: user.fullName, createdAt: now,
      }
      tx.set(payRef, clean(payment))
      writeAudit(tx, user, {
        action: input.kind === 'Deposit' ? 'Deposit received' : 'Payment recorded',
        entity: 'Payment', entityId: input.id, reference: receiptNumber,
        summary: `${input.account.name} — ${payment.amount.toFixed(2)} by ${input.method.name}${applied.length ? ` → ${applied.map((a) => a.invoiceNumber).join(', ')}` : ''}${toN(payment.unallocated) > 0 ? ` (credit ${payment.unallocated.toFixed(2)})` : ''}`,
      })
      return { already: false, number: receiptNumber }
    })
    return { ok: true, id: input.id, message: result.already ? `Already recorded as ${result.number} — not duplicated.` : `Receipt ${result.number} recorded.` }
  } catch (error) {
    return failure(error, 'The payment could not be recorded.')
  }
}

/** Applies unallocated account credit (from a payment/deposit or a credit note) to an invoice. */
export async function applyCredit(
  user: User,
  input: { sourceType: 'payment' | 'creditNote'; sourceId: string; invoiceId: string; amount: number },
): Promise<BillingResult> {
  try {
    const amountN = toN(input.amount)
    await runTransaction(db, async (tx) => {
      const sourceRef = doc(db, input.sourceType === 'payment' ? BCOL.payments : BCOL.creditNotes, input.sourceId)
      const [sourceSnap, invSnap] = await Promise.all([tx.get(sourceRef), tx.get(invRef(input.invoiceId))])
      const source = sourceSnap.data() as (BillPayment | CreditNote) | undefined
      const invoice = invSnap.data() as SalesDocument | undefined
      if (!source || source.state !== (input.sourceType === 'payment' ? 'Posted' : 'Issued')) throw new Error('The credit source is not available.')
      if (amountN > toN(source.unallocated)) throw new Error(`Only ${source.unallocated.toFixed(2)} credit is available.`)
      assertApplicable(invoice, source.accountId, amountN, 'Credit')
      const now = new Date().toISOString()
      const field = input.sourceType === 'payment' ? 'amountPaid' : 'amountCredited'
      tx.update(invRef(invoice!.id), {
        [field]: fromN(toN(invoice![field]) + amountN),
        balance: fromN(toN(invoice!.balance) - amountN),
        updatedAt: now,
      })
      tx.update(sourceRef, clean({
        unallocated: fromN(toN(source.unallocated) - amountN),
        allocations: [...source.allocations, { invoiceId: invoice!.id, invoiceNumber: invoice!.number ?? '', amount: fromN(amountN), at: now, byName: user.fullName }],
      }))
      const ref = 'receiptNumber' in source ? source.receiptNumber : source.number
      writeAudit(tx, user, { action: 'Account credit applied', entity: input.sourceType === 'payment' ? 'Payment' : 'Credit note', entityId: input.sourceId, reference: ref, summary: `${fromN(amountN).toFixed(2)} → ${invoice!.number}` })
    })
    return { ok: true, id: input.invoiceId, message: 'Credit applied.' }
  } catch (error) {
    return failure(error, 'The credit could not be applied.')
  }
}

/** Reverses a posted payment: restores every invoice balance it paid. The receipt is kept, marked reversed. */
export async function reversePayment(user: User, id: string, reason: string): Promise<BillingResult> {
  try {
    if (!reason.trim()) return { ok: false, error: 'A reason is required to reverse a payment.' }
    await runTransaction(db, async (tx) => {
      const ref = doc(db, BCOL.payments, id)
      const snap = await tx.get(ref)
      const payment = snap.data() as BillPayment | undefined
      if (!payment) throw new Error('Payment not found.')
      if (payment.state === 'Reversed') return
      if (toN(payment.refunded) > 0) throw new Error('Part of this payment was refunded. It cannot be reversed.')
      const invSnaps = await Promise.all(payment.allocations.map((a) => tx.get(invRef(a.invoiceId))))
      const now = new Date().toISOString()
      const totals = new Map<string, number>()
      payment.allocations.forEach((a) => totals.set(a.invoiceId, (totals.get(a.invoiceId) ?? 0) + toN(a.amount)))
      const seen = new Set<string>()
      invSnaps.forEach((s) => {
        const inv = s.data() as SalesDocument | undefined
        if (!inv || seen.has(inv.id)) return
        seen.add(inv.id)
        const n = totals.get(inv.id) ?? 0
        tx.update(invRef(inv.id), { amountPaid: fromN(Math.max(0, toN(inv.amountPaid) - n)), balance: fromN(toN(inv.balance) + n), updatedAt: now })
      })
      tx.update(ref, { state: 'Reversed', unallocated: 0, reversedAt: now, reversedByName: user.fullName, reversalReason: reason.trim() })
      writeAudit(tx, user, { action: 'Payment reversed', entity: 'Payment', entityId: id, reference: payment.receiptNumber, summary: `${payment.accountName} — ${payment.amount.toFixed(2)}`, reason: reason.trim() })
    })
    return { ok: true, id, message: 'Payment reversed and invoice balances restored.' }
  } catch (error) {
    return failure(error, 'The payment could not be reversed.')
  }
}

export type CreditNoteInput = { id: string; account: BillingAccount; invoiceId?: string; amount: number; reason: string; date: string }

/** Issues a credit note. It is applied to the chosen invoice up to its balance; the rest is account credit. */
export async function issueCreditNote(user: User, config: BillingConfig, input: CreditNoteInput): Promise<BillingResult> {
  try {
    const amountN = toN(input.amount)
    if (amountN <= 0) return { ok: false, error: 'Enter the credit amount.' }
    if (!input.reason.trim()) return { ok: false, error: 'A reason is required for a credit note.' }
    const result = await runTransaction(db, async (tx) => {
      const ref = doc(db, BCOL.creditNotes, input.id)
      const existing = await tx.get(ref)
      if (existing.exists()) return { already: true, number: (existing.data() as CreditNote).number }
      const invoice = input.invoiceId ? ((await tx.get(invRef(input.invoiceId))).data() as SalesDocument | undefined) : undefined
      if (input.invoiceId) {
        if (!invoice || invoice.state !== 'Issued') throw new Error('Credit notes can only be raised against issued invoices.')
        if (invoice.accountId !== input.account.id) throw new Error('The invoice belongs to a different account.')
        if (amountN > toN(invoice.total) - toN(invoice.amountCredited)) throw new Error(`Credit cannot exceed the invoice total not yet credited (${fromN(toN(invoice.total) - toN(invoice.amountCredited)).toFixed(2)}).`)
      }
      const plan = await planNumbers(tx, config, 'creditNote', 1)
      const now = new Date().toISOString()
      const number = plan.numbers[0]
      commitNumbers(tx, plan, [input.id])
      const appliedN = invoice ? Math.min(amountN, toN(invoice.balance)) : 0
      const allocations: Allocation[] = []
      if (invoice && appliedN > 0) {
        tx.update(invRef(invoice.id), { amountCredited: fromN(toN(invoice.amountCredited) + appliedN), balance: fromN(toN(invoice.balance) - appliedN), updatedAt: now })
        allocations.push({ invoiceId: invoice.id, invoiceNumber: invoice.number ?? '', amount: fromN(appliedN), at: now, byName: user.fullName })
      }
      const note: CreditNote = {
        id: input.id, number, accountId: input.account.id, accountName: input.account.name,
        invoiceId: invoice?.id, invoiceNumber: invoice?.number ?? undefined, date: input.date, reason: input.reason.trim(),
        amount: fromN(amountN), allocations, unallocated: fromN(amountN - appliedN), refunded: 0, state: 'Issued',
        issuer: { business: config.business, appearance: config.appearance, regional: config.regional },
        createdById: user.id, createdByName: user.fullName, createdAt: now,
      }
      tx.set(ref, clean(note))
      writeAudit(tx, user, { action: 'Credit note issued', entity: 'Credit note', entityId: input.id, reference: number, summary: `${input.account.name} — ${note.amount.toFixed(2)}${invoice ? ` against ${invoice.number}` : ''}`, reason: note.reason })
      return { already: false, number }
    })
    return { ok: true, id: input.id, message: result.already ? `Already issued as ${result.number}.` : `Credit note ${result.number} issued.` }
  } catch (error) {
    return failure(error, 'The credit note could not be issued.')
  }
}

export async function voidCreditNote(user: User, id: string, reason: string): Promise<BillingResult> {
  try {
    if (!reason.trim()) return { ok: false, error: 'A reason is required.' }
    await runTransaction(db, async (tx) => {
      const ref = doc(db, BCOL.creditNotes, id)
      const note = (await tx.get(ref)).data() as CreditNote | undefined
      if (!note) throw new Error('Credit note not found.')
      if (note.state === 'Void') return
      if (toN(note.refunded) > 0) throw new Error('This credit was refunded and cannot be voided.')
      const snaps = await Promise.all(note.allocations.map((a) => tx.get(invRef(a.invoiceId))))
      const totals = new Map<string, number>()
      note.allocations.forEach((a) => totals.set(a.invoiceId, (totals.get(a.invoiceId) ?? 0) + toN(a.amount)))
      const now = new Date().toISOString()
      const seen = new Set<string>()
      snaps.forEach((s) => {
        const inv = s.data() as SalesDocument | undefined
        if (!inv || seen.has(inv.id)) return
        seen.add(inv.id)
        const n = totals.get(inv.id) ?? 0
        tx.update(invRef(inv.id), { amountCredited: fromN(Math.max(0, toN(inv.amountCredited) - n)), balance: fromN(toN(inv.balance) + n), updatedAt: now })
      })
      tx.update(ref, { state: 'Void', unallocated: 0, voidedAt: now, voidedByName: user.fullName, voidReason: reason.trim() })
      writeAudit(tx, user, { action: 'Credit note voided', entity: 'Credit note', entityId: id, reference: note.number, summary: `${note.accountName} — ${note.amount.toFixed(2)}`, reason: reason.trim() })
    })
    return { ok: true, id, message: 'Credit note voided and invoice balances restored.' }
  } catch (error) {
    return failure(error, 'The credit note could not be voided.')
  }
}

export type RefundInput = { id: string; sourceType: 'payment' | 'creditNote'; sourceId: string; amount: number; method: PaymentMethod; reference?: string; reason: string; date: string }

/** Refunds unallocated credit back to the customer. Only unallocated money can be refunded. */
export async function recordRefund(user: User, config: BillingConfig, input: RefundInput): Promise<BillingResult> {
  try {
    const amountN = toN(input.amount)
    if (amountN <= 0) return { ok: false, error: 'Enter the refund amount.' }
    if (!input.reason.trim()) return { ok: false, error: 'A reason is required for a refund.' }
    const result = await runTransaction(db, async (tx) => {
      const ref = doc(db, BCOL.refunds, input.id)
      const existing = await tx.get(ref)
      if (existing.exists()) return { already: true, number: (existing.data() as Refund).number }
      const sourceRef = doc(db, input.sourceType === 'payment' ? BCOL.payments : BCOL.creditNotes, input.sourceId)
      const source = (await tx.get(sourceRef)).data() as (BillPayment | CreditNote) | undefined
      if (!source || source.state !== (input.sourceType === 'payment' ? 'Posted' : 'Issued')) throw new Error('The credit source is not available.')
      if (amountN > toN(source.unallocated)) throw new Error(`Only ${source.unallocated.toFixed(2)} unallocated credit can be refunded.`)
      const plan = await planNumbers(tx, config, 'refund', 1)
      const now = new Date().toISOString()
      const number = plan.numbers[0]
      commitNumbers(tx, plan, [input.id])
      const sourceNumber = 'receiptNumber' in source ? source.receiptNumber : source.number
      tx.update(sourceRef, { unallocated: fromN(toN(source.unallocated) - amountN), refunded: fromN(toN(source.refunded) + amountN) })
      const refund: Refund = {
        id: input.id, number, accountId: source.accountId, accountName: source.accountName, sourceType: input.sourceType, sourceId: input.sourceId,
        sourceNumber, date: input.date, amount: fromN(amountN), methodId: input.method.id, methodName: input.method.name,
        reference: input.reference?.trim() || undefined, reason: input.reason.trim(), createdById: user.id, createdByName: user.fullName, createdAt: now,
      }
      tx.set(ref, clean(refund))
      writeAudit(tx, user, { action: 'Refund recorded', entity: 'Refund', entityId: input.id, reference: number, summary: `${source.accountName} — ${refund.amount.toFixed(2)} from ${sourceNumber} by ${input.method.name}`, reason: refund.reason })
      return { already: false, number }
    })
    return { ok: true, id: input.id, message: result.already ? `Already recorded as ${result.number}.` : `Refund ${result.number} recorded.` }
  } catch (error) {
    return failure(error, 'The refund could not be recorded.')
  }
}
